import type {
  AestheticsRecord,
  ActorListResponse,
  ActorProfile,
  ActorRelationshipsResponse,
  ActorSearchResponse,
  AtprotoRecord,
  AuthorFeedResponse,
  ContentRecord,
  FeedPost,
  PostSearchResponse,
  PostLikesResponse,
  PostRepostsResponse,
  PostThreadResponse,
  ProfilesResponse,
  SkyspaceProfileContent,
} from '../domain/types';

const CACHED_APPVIEW = 'https://public.api.bsky.app';
const DIRECT_APPVIEW = 'https://api.bsky.app';
const AESTHETICS_COLLECTION = 'me.skyspace.aesthetics';
const CONTENT_COLLECTION = 'me.skyspace.content';

export class BlueskyApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly nsid: string,
  ) {
    super(message);
    this.name = 'BlueskyApiError';
  }
}

export class PublicBlueskyClient {
  constructor(
    private readonly fetcher: typeof fetch = (...args) => globalThis.fetch(...args),
    private readonly random: () => number = Math.random,
  ) {}

  private async request<T>(
    nsid: string,
    params: Record<string, string | number | undefined>,
    direct = false,
    signal?: AbortSignal,
  ): Promise<T> {
    const endpoint = new URL(`/xrpc/${nsid}`, direct ? DIRECT_APPVIEW : CACHED_APPVIEW);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        endpoint.searchParams.set(key, String(value));
      }
    });

    const response = await this.fetcher(endpoint, {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) {
      throw new BlueskyApiError(
        `Bluesky returned HTTP ${response.status}.`,
        response.status,
        nsid,
      );
    }
    return response.json() as Promise<T>;
  }

  getProfile(handle: string, signal?: AbortSignal): Promise<ActorProfile> {
    return this.request<ActorProfile>('app.bsky.actor.getProfile', { actor: handle }, false, signal);
  }

  async getProfiles(handles: string[], signal?: AbortSignal): Promise<ActorProfile[]> {
    const unique = [...new Set(handles.map(handle => handle.toLowerCase()))].slice(0, 25);
    if (unique.length === 0) return [];
    const endpoint = new URL('/xrpc/app.bsky.actor.getProfiles', CACHED_APPVIEW);
    unique.forEach(actor => endpoint.searchParams.append('actors', actor));
    const response = await this.fetcher(endpoint, {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) {
      throw new BlueskyApiError(
        `Bluesky returned HTTP ${response.status}.`,
        response.status,
        'app.bsky.actor.getProfiles',
      );
    }
    return ((await response.json()) as ProfilesResponse).profiles || [];
  }

  getAuthorFeed(handle: string, limit = 20, signal?: AbortSignal): Promise<AuthorFeedResponse> {
    return this.request<AuthorFeedResponse>('app.bsky.feed.getAuthorFeed', {
      actor: handle,
      limit,
      filter: 'posts_no_replies',
    }, false, signal);
  }

  async getPostLikers(uri: string, limit = 100, signal?: AbortSignal): Promise<ActorProfile[]> {
    const response = await this.request<PostLikesResponse>('app.bsky.feed.getLikes', {
      uri,
      limit: Math.min(Math.max(limit, 1), 100),
    }, false, signal);
    return (response.likes || []).map(item => item.actor);
  }

  async getPostReposters(uri: string, limit = 100, signal?: AbortSignal): Promise<ActorProfile[]> {
    const response = await this.request<PostRepostsResponse>('app.bsky.feed.getRepostedBy', {
      uri,
      limit: Math.min(Math.max(limit, 1), 100),
    }, false, signal);
    return response.repostedBy || [];
  }

  searchActors(
    term: string,
    limit = 25,
    signal?: AbortSignal,
    cursor?: string,
  ): Promise<ActorSearchResponse> {
    return this.request<ActorSearchResponse>('app.bsky.actor.searchActors', {
      term,
      limit,
      cursor,
    }, false, signal);
  }

  searchPosts(
    query: string,
    limit = 50,
    signal?: AbortSignal,
    cursor?: string,
  ): Promise<PostSearchResponse> {
    return this.request<PostSearchResponse>(
      'app.bsky.feed.searchPosts',
      { q: query, limit, cursor },
      true,
      signal,
    );
  }

  getFollowers(
    handle: string,
    limit = 100,
    signal?: AbortSignal,
    cursor?: string,
  ): Promise<ActorListResponse> {
    return this.request<ActorListResponse>('app.bsky.graph.getFollowers', {
      actor: handle,
      limit: Math.min(limit, 100),
      cursor,
    }, false, signal);
  }

  getFollows(
    handle: string,
    limit = 100,
    signal?: AbortSignal,
    cursor?: string,
  ): Promise<ActorListResponse> {
    return this.request<ActorListResponse>('app.bsky.graph.getFollows', {
      actor: handle,
      limit: Math.min(limit, 100),
      cursor,
    }, false, signal);
  }

  private async getRelationships(
    handle: string,
    dids: string[],
    signal?: AbortSignal,
  ): Promise<ActorRelationshipsResponse> {
    const endpoint = new URL('/xrpc/app.bsky.graph.getRelationships', CACHED_APPVIEW);
    endpoint.searchParams.set('actor', handle);
    dids.slice(0, 30).forEach(did => endpoint.searchParams.append('others', did));
    const response = await this.fetcher(endpoint, {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) {
      throw new BlueskyApiError(
        `Bluesky returned HTTP ${response.status}.`,
        response.status,
        'app.bsky.graph.getRelationships',
      );
    }
    return response.json() as Promise<ActorRelationshipsResponse>;
  }

  async getMutualSummary(
    profile: Pick<ActorProfile, 'handle' | 'followersCount' | 'followsCount'>,
    sampleLimit = 8,
    signal?: AbortSignal,
    includeProfile: (profile: ActorProfile) => boolean = () => true,
  ): Promise<{ count: number; profiles: ActorProfile[] }> {
    const useFollows = (profile.followsCount ?? Number.POSITIVE_INFINITY)
      <= (profile.followersCount ?? Number.POSITIVE_INFINITY);
    const boundedSampleLimit = Math.max(0, Math.floor(sampleLimit));
    const sample: ActorProfile[] = [];
    const seen = new Set<string>();
    let eligibleCount = 0;
    let count = 0;
    let cursor: string | undefined;
    do {
      const page = useFollows
        ? await this.getFollows(profile.handle, 100, signal, cursor)
        : await this.getFollowers(profile.handle, 100, signal, cursor);
      const candidates = useFollows ? page.follows || [] : page.followers || [];
      const chunks = Array.from(
        { length: Math.ceil(candidates.length / 30) },
        (_, index) => candidates.slice(index * 30, index * 30 + 30),
      );
      const responses = await Promise.all(chunks.map(chunk => (
        this.getRelationships(profile.handle, chunk.map(actor => actor.did), signal)
      )));
      const profilesByDid = new Map(candidates.map(actor => [actor.did, actor]));
      responses.flatMap(response => response.relationships || []).forEach(relationship => {
        if (
          !relationship.did
          || !relationship.following
          || !relationship.followedBy
          || seen.has(relationship.did)
        ) return;
        seen.add(relationship.did);
        count += 1;
        const actor = profilesByDid.get(relationship.did);
        if (!actor || !includeProfile(actor)) return;
        eligibleCount += 1;
        if (sample.length < boundedSampleLimit) {
          sample.push(actor);
          return;
        }
        if (boundedSampleLimit === 0) return;
        const randomIndex = Math.floor(
          Math.max(0, Math.min(0.9999999999999999, this.random())) * eligibleCount,
        );
        if (randomIndex < boundedSampleLimit) sample[randomIndex] = actor;
      });
      cursor = page.cursor;
    } while (cursor);
    return { count, profiles: sample };
  }

  async getFollowCandidates(
    handle: string,
    signal?: AbortSignal,
  ): Promise<{ profiles: ActorProfile[]; mutualDids: string[] }> {
    const profiles = new Map<string, ActorProfile>();
    let cursor: string | undefined;
    do {
      const page = await this.getFollows(handle, 100, signal, cursor);
      (page.follows || []).forEach(actor => profiles.set(actor.did, actor));
      cursor = page.cursor;
    } while (cursor && profiles.size < 2_000);

    const candidates = [...profiles.values()];
    const chunks = Array.from(
      { length: Math.ceil(candidates.length / 30) },
      (_, index) => candidates.slice(index * 30, index * 30 + 30),
    );
    const relationships = await Promise.all(chunks.map(chunk => (
      this.getRelationships(handle, chunk.map(actor => actor.did), signal)
    )));
    const mutualDids = relationships
      .flatMap(response => response.relationships || [])
      .flatMap(relationship => (
        relationship.did && relationship.following && relationship.followedBy
          ? [relationship.did]
          : []
      ));

    return { profiles: candidates, mutualDids };
  }

  getPostThread(uri: string, depth = 1, signal?: AbortSignal): Promise<PostThreadResponse> {
    return this.request<PostThreadResponse>('app.bsky.feed.getPostThread', {
      uri,
      depth,
      parentHeight: 0,
    }, false, signal);
  }

  private async getDidDocument(did: string, signal?: AbortSignal): Promise<{
    service?: Array<{
      id?: string;
      type?: string;
      serviceEndpoint?: string;
    }>;
  }> {
    let url: string;
    if (did.startsWith('did:plc:')) {
      url = `https://plc.directory/${encodeURIComponent(did)}`;
    } else if (did.startsWith('did:web:')) {
      const identifier = did.slice('did:web:'.length);
      const [host, ...path] = identifier.split(':').map(decodeURIComponent);
      url = path.length
        ? `https://${host}/${path.join('/')}/did.json`
        : `https://${host}/.well-known/did.json`;
    } else {
      throw new BlueskyApiError('This type of Bluesky identity is not supported yet.', 400, 'did.resolve');
    }

    const response = await this.fetcher(url, {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) {
      throw new BlueskyApiError(
        `Identity directory returned HTTP ${response.status}.`,
        response.status,
        'did.resolve',
      );
    }
    return response.json();
  }

  private async getPdsEndpoint(did: string, signal?: AbortSignal): Promise<string> {
    const document = await this.getDidDocument(did, signal);
    const service = document.service?.find(entry => (
      entry.id === '#atproto'
      || entry.id === '#atproto_pds'
      || entry.type === 'AtprotoPersonalDataServer'
    ));
    const endpoint = service?.serviceEndpoint;
    if (!endpoint) {
      throw new BlueskyApiError(
        'This Bluesky account does not advertise a personal data server.',
        404,
        'did.resolve',
      );
    }
    const url = new URL(endpoint);
    if (url.protocol !== 'https:') {
      throw new BlueskyApiError('The personal data server is not HTTPS.', 400, 'did.resolve');
    }
    return url.origin;
  }

  private async listLatestRecord<T>(
    pds: string,
    did: string,
    collection: string,
    signal?: AbortSignal,
  ): Promise<AtprotoRecord<T> | undefined> {
    const endpoint = new URL('/xrpc/com.atproto.repo.listRecords', pds);
    endpoint.searchParams.set('repo', did);
    endpoint.searchParams.set('collection', collection);
    endpoint.searchParams.set('limit', '10');

    const response = await this.fetcher(endpoint, {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (response.status === 404 || response.status === 400) return undefined;
    if (!response.ok) {
      throw new BlueskyApiError(
        `The personal data server returned HTTP ${response.status}.`,
        response.status,
        'com.atproto.repo.listRecords',
      );
    }

    const data = await response.json() as { records?: Array<AtprotoRecord<T>> };
    if (!data.records?.length) return undefined;
    return [...data.records].sort((left, right) => {
      const leftTime = Date.parse((left.value as { createdAt?: string }).createdAt || '') || 0;
      const rightTime = Date.parse((right.value as { createdAt?: string }).createdAt || '') || 0;
      return rightTime - leftTime;
    })[0];
  }

  async getSkyspaceContent(did: string, signal?: AbortSignal): Promise<SkyspaceProfileContent> {
    const pds = await this.getPdsEndpoint(did, signal);
    const [aesthetics, content] = await Promise.all([
      this.listLatestRecord<AestheticsRecord>(pds, did, AESTHETICS_COLLECTION, signal),
      this.listLatestRecord<ContentRecord>(pds, did, CONTENT_COLLECTION, signal),
    ]);
    const cid = aesthetics?.value.bg?.image?.ref?.$link;
    if (aesthetics?.value.bg && cid) {
      const blobUrl = new URL('/xrpc/com.atproto.sync.getBlob', pds);
      blobUrl.searchParams.set('did', did);
      blobUrl.searchParams.set('cid', cid);
      aesthetics.value = {
        ...aesthetics.value,
        bg: {
          ...aesthetics.value.bg,
          resolvedUrl: blobUrl.href,
        },
      };
    }
    return { aesthetics, content };
  }
}

export const publicBluesky = new PublicBlueskyClient();

export function uniqueActorsFromPosts(posts: FeedPost[]): ActorProfile[] {
  const actors = new Map<string, ActorProfile>();
  posts.forEach(post => {
    if (post.author?.handle && !actors.has(post.author.handle)) {
      actors.set(post.author.handle, post.author);
    }
  });
  return [...actors.values()];
}
